import React, { useEffect, useState } from 'react';
import {
  getAllDocumentTypes,
  createDocumentType,
  updateDocumentType,
  deleteDocumentType,
} from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface DocType {
  _id: string;
  key: string;
  name: string;
  description?: string;
}

const DocumentTypes: React.FC = () => {
  const [list, setList] = useState<DocType[]>([]);
  const [form, setForm] = useState({ key: '', name: '', description: '' });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    const data = await getAllDocumentTypes();
    setList(data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await updateDocumentType(editingId, form);
        setEditingId(null);
        alert('Document type updated');
      } else {
        await createDocumentType(form);
        alert('Document type created');
      }
      setForm({ key: '', name: '', description: '' });
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save document type');
    }
  };

  const handleEdit = (d: DocType) => {
    setEditingId(d._id);
    setForm({ key: d.key, name: d.name, description: d.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this document type?')) return;
    await deleteDocumentType(id);
    load();
  };

  return (
    <>
      <Header title="Document Types" />
      <div className="page-container">
        <div className="page-header">
          <h1>Document Types</h1>
          <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ key: '', name: '', description: '' }); }} className="btn-primary">
            {showForm ? 'Cancel' : '+ Add New Document Type'}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showForm && (
          <div className="form-container">
            <h2>{editingId ? 'Edit Document Type' : 'Create New Document Type'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Key *</label>
                  <input
                    type="text"
                    value={form.key}
                    onChange={(e) => setForm({ ...form, key: e.target.value })}
                    required
                    disabled={editingId !== null}
                    placeholder="e.g., sample_pdf"
                  />
                </div>

                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    placeholder="Human readable name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">{editingId ? 'Update Document Type' : 'Create Document Type'}</button>
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm({ key: '', name: '', description: '' }); }} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d._id}>
                  <td>{d.key}</td>
                  <td>{d.name}</td>
                  <td>{d.description}</td>
                  <td className="action-cell">
                    <button onClick={() => handleEdit(d)} className="btn-small btn-edit">Edit</button>
                    <button onClick={() => handleDelete(d._id)} className="btn-small btn-delete">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DocumentTypes;
